import { Injectable, Injector } from '@angular/core';
import { AbstractDataProvider } from '@epicuro-next/data-providers/base-data-provider';
import { isObservable, Observable, of } from 'rxjs';

import { DATA_PROVIDERS_TOKENS_MAP } from './providers-registry';

export interface IDataProvidersResolver {
  resolve(provider: string, componentInjector?: Injector): AbstractDataProvider;
  setOneTimeValue(provider: string, value: unknown): void;
}

@Injectable({
  providedIn: 'root',
})
export class DataProvidersResolver implements IDataProvidersResolver {
  private readonly oneTimeValues = new Map<string, unknown>();

  constructor(private injector: Injector) {}

  public resolve(provider: string, componentInjector?: Injector) {
    if (this.oneTimeValues.has(provider)) {
      let value = this.oneTimeValues.get(provider);
      this.oneTimeValues.delete(provider);
      const read: AbstractDataProvider['read'] = <T>() => {
        if (!isObservable(value)) {
          value = of(value);
        }
        return value as Observable<T>;
      };

      const oneTimeAbstractDataProvider: AbstractDataProvider = {
        read,
        write: () => {
          throw new Error('One should not write to a one time provider');
        },
      };

      return oneTimeAbstractDataProvider;
    }

    const token = DATA_PROVIDERS_TOKENS_MAP.get(provider);

    if (!token) {
      throw new Error(`No data provider found for ${provider}`);
    }

    const injector = componentInjector ?? this.injector;
    const service: AbstractDataProvider = injector.get(token);

    return service;
  }

  public setOneTimeValue(provider: string, value: unknown): void {
    this.oneTimeValues.set(provider, value);
  }
}
